import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  makeStyles,
  Grid,
  Button,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";

import { Controller, useForm, useWatch } from "react-hook-form";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { Error } from "../../../common/Error";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { segmentList } from "@ofqual/portal-core/core/utils/listHelpers";
import { formatAsTodayYesterdayOrDate } from "@ofqual/portal-core/core/utils/dateUtils";
import {
  Activity,
  ActivityFeedItem,
  ActivityFeedItemDefinition,
  ActivitySegmentHeader,
} from "@ofqual/portal-activity-feed";
import { buildDefinitions } from "../buildDefinitions";
import { authSelector, feedSelector } from "../../../../redux/selectors";
import { ActivityType } from "../../../../domain/ActivityType";
import { SocItem } from "../../../../domain/SocItem";
import { sendMessage } from "../../../../redux/actions";
import { useAuth } from "@ofqual/portal-core/core/hooks";
import { UserType } from "../../../../redux/types";

const useStyles = makeStyles((theme) => ({
  actions: {
    padding: 15,
  },
}));

export const SendMessageDialog: React.FC<{
  onClose: () => void;
  resource: SocItem;
}> = ({ onClose, resource }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { userType } = useAuth(authSelector);

  const { handleSubmit, control, errors } = useForm({
    defaultValues: {
      message: null,
      files: [],
    },
  });

  const handleSendMessage = (data: { files: File[]; message: string }) => {
    dispatch(sendMessage(resource.id, data.files, data.message));
    onClose();
  };

  const definitions: ActivityFeedItemDefinition<any>[] = buildDefinitions(
    () => {},
    resource
  );

  const Values = (): Activity[] => {
    const activity = useSelector(feedSelector);
    const value = activity[resource.id];
    let items = value.response.data.filter(
      (item) =>
        item.activityType === ActivityType.SocMessageSentAO ||
        item.activityType === ActivityType.SocMessageSentOfqual
    );

    items.sort((a, b) => {
      let dateA = new Date(a.createdDate);
      let dateB = new Date(b.createdDate);
      return dateB.getTime() - dateA.getTime();
    });

    return items;
  };

  const segmentedActivities = segmentList(
    Values(),
    (item) => formatAsTodayYesterdayOrDate(item.createdDate) || ""
  );

  const confirmSend = useWatch({
    control,
    name: "confirmSend",
  });

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle data-test="activity-title">Send Message</DialogTitle>
      <Divider />
      <form>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="message"
                  rules={{ required: true }}
                  render={({ onChange, value }) => (
                    <MarkdownEditor
                      onChange={onChange}
                      value={value}
                      dataTest="create-message-text-field"
                    />
                  )}
                />
              </Grid>
              {errors.message && (
                <Error data-test="create-message-text-field-error">
                  This field is required
                </Error>
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="files"
                as={
                  <FileInput
                    multiple={true}
                    data-test="create-message-add-attachment-button"
                  />
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions className={classes.actions}>
          {userType !== UserType.AO && (
            <Grid item xs={12}>
              <Controller
                control={control}
                rules={{ required: true }}
                name="confirmSend"
                render={({ onChange }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        data-test="confirm-send-checkbox"
                        onChange={(e) => {
                          onChange(e.target.checked);
                        }}
                      />
                    }
                    label="I understand that this communication is directed at, and visible to, the awarding organisation *"
                    labelPlacement="start"
                    style={{ color: !confirmSend ? "red" : undefined }}
                  />
                )}
              />
            </Grid>
          )}
          <Button
            onClick={onClose}
            color="primary"
            data-test="close-send-message-dialog"
          >
            Close
          </Button>
          <Button
            onClick={handleSubmit(handleSendMessage)}
            color="primary"
            data-test="send-message-button"
          >
            Send
          </Button>
        </DialogActions>
      </form>
      <Divider />
      <DialogContent>
        {segmentedActivities.map((segment) => (
          <div key={segment.label}>
            <ActivitySegmentHeader label={segment.label} />
            {segment.data.map((item) => (
              <ActivityFeedItem
                key={item.id}
                activity={item}
                definition={definitions.find(
                  (definition) => definition.activityType === item.activityType
                )}
              />
            ))}
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
};
